import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const schema = JSON.stringify([
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How Much Protein Do Men Over 60 Actually Need?",
    "author": { "@type": "Person", "name": "Mason Hale" },
    "datePublished": "2026-01-12",
    "dateModified": "2026-04-06",
    "publisher": { "@type": "Organization", "name": "Mason Hale Fitness" },
    "url": "https://masonhalefit.com/blog/protein-intake-for-men-over-60"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much protein does a man over 60 need per day?",
        "acceptedAnswer": { "@type": "Answer", "text": "Men over 60 who strength train should aim for 0.7-1.0 grams of protein per pound of body weight per day. A 180-pound man needs roughly 126-180 grams daily. This is significantly higher than the outdated RDA of 0.36g/lb." }
      },
      {
        "@type": "Question",
        "name": "Is too much protein bad for older men?",
        "acceptedAnswer": { "@type": "Answer", "text": "For men with healthy kidneys, higher protein intake is safe and beneficial. The concern about protein damaging kidneys applies mainly to those with pre-existing kidney disease. Always consult your doctor if you have kidney concerns." }
      },
      {
        "@type": "Question",
        "name": "What are the best protein sources for men over 60?",
        "acceptedAnswer": { "@type": "Answer", "text": "Eggs, chicken breast, Greek yogurt, cottage cheese, salmon, lean beef, and whey protein are all excellent sources. Aim for complete proteins that contain all essential amino acids, especially leucine which triggers muscle protein synthesis." }
      },
      {
        "@type": "Question",
        "name": "Should men over 60 use protein shakes?",
        "acceptedAnswer": { "@type": "Answer", "text": "Protein shakes are a convenient tool, not a requirement. If you can hit your protein targets through whole foods, great. If not, a quality whey or plant-based protein shake can help fill the gap without much effort." }
      }
    ]
  }
]);

const tocItems = [
  { id: 'why-protein', label: 'Why Protein Needs Increase After 60' },
  { id: 'how-much', label: 'How Much Protein You Actually Need' },
  { id: 'best-sources', label: 'Best Protein Sources for Men Over 60' },
  { id: 'timing', label: 'Protein Timing: Does It Matter?' },
  { id: 'sample-day', label: 'Sample High-Protein Day of Eating' },
  { id: 'supplements', label: 'When to Use Protein Supplements' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const related = [
  { slug: 'best-protein-powder-for-men-over-60', title: 'Best Protein Powder for Men Over 60 (2026 Guide)', category: 'Nutrition' },
  { slug: 'meal-plan-for-men-over-60', title: 'Simple Meal Plan for Men Over 60 (Build Muscle, Lose Fat)', category: 'Nutrition' },
  { slug: 'how-to-build-muscle-after-60', title: 'How to Build Muscle After 60: The Science-Backed Approach', category: 'Strength Training' },
];

export default function ProteinIntakePage() {
  return (
    <BlogLayout
      title="Protein Intake for Men Over 60: How Much You Need"
      description="How much protein do men over 60 actually need? The science-backed answer, best sources, and a sample high-protein meal plan. Stop leaving gains on the table."
      keywords="protein intake for men over 60, how much protein over 60, protein for older men"
      canonicalPath="/blog/protein-intake-for-men-over-60"
      datePublished="2026-01-12"
      dateModified="2026-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="How Much Protein Do Men Over 60 Actually Need?" />

      <article>
        <header className="mb-8">
          <span className="text-[#c8a96e] text-xs font-bold tracking-[0.25em] uppercase">Nutrition</span>
          <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mt-2 mb-4">
            How Much Protein Do Men Over 60 Actually Need?
          </h1>
          <p className="text-gray-400 text-base leading-relaxed">
            If you're strength training and not hitting your protein targets, you're leaving results on the table. Plain and simple. Protein is the raw material your body uses to build and repair muscle — and after 60, you need more of it than you think.
          </p>
          <p className="text-gray-400 text-base leading-relaxed mt-3">
            The government's recommended daily allowance (RDA) for protein is embarrassingly low for active older men. Let's look at what the science actually says.
          </p>
        </header>

        <TableOfContents items={tocItems} />

        <section id="why-protein" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">Why Protein Needs Increase After 60</h2>
          <p className="text-gray-400 leading-relaxed mb-3">
            After 60, your body becomes less efficient at using dietary protein to build muscle. This phenomenon is called "anabolic resistance" — your muscles don't respond as strongly to the same protein stimulus that worked when you were younger.
          </p>
          <p className="text-gray-400 leading-relaxed mb-3">
            Research from the <a href="https://www.nia.nih.gov/health/exercise-physical-activity/exercise-older-adults" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:underline">National Institute on Aging</a> confirms that older adults need higher protein intake to achieve the same muscle-building response as younger people. The solution isn't to give up — it's to eat more protein.
          </p>
          <p className="text-gray-400 leading-relaxed mb-4">
            Without adequate protein intake, your body will break down existing muscle tissue for fuel — the opposite of what you want. This is called sarcopenia, and it's one of the biggest threats to health and independence after 60.
          </p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 mb-4 ml-2">
            <li>Anabolic resistance means you need more protein per meal to trigger muscle synthesis</li>
            <li>Sarcopenia (muscle loss) accelerates without adequate protein</li>
            <li>Protein supports immune function, bone density, and hormone production</li>
            <li>Higher protein intake helps preserve muscle during weight loss</li>
          </ul>
        </section>

        <section id="how-much" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">How Much Protein You Actually Need</h2>
          <p className="text-gray-400 leading-relaxed mb-3">
            The government RDA is 0.36 grams per pound of body weight. For a 180-pound man, that's just 65 grams per day. That's the minimum to prevent deficiency — not the amount to build muscle and stay strong.
          </p>
          <p className="text-gray-400 leading-relaxed mb-4">
            For men over 60 who strength train, the research-backed target is <strong className="text-white">0.7-1.0 grams per pound of body weight per day</strong>.
          </p>

          <div className="bg-[#111111] border border-[#c8a96e]/20 rounded-lg p-6 mb-6">
            <h3 className="text-white font-bold text-lg mb-4">Protein Targets by Body Weight</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-[#c8a96e] font-bold py-2 pr-4">Body Weight</th>
                    <th className="text-left text-gray-400 font-semibold py-2">Daily Protein Target</th>
                  </tr>
                </thead>
                <tbody className="text-gray-400">
                  {[
                    ['150 lbs', '105-150g per day'],
                    ['170 lbs', '119-170g per day'],
                    ['190 lbs', '133-190g per day'],
                    ['210 lbs', '147-210g per day'],
                    ['230 lbs', '161-230g per day'],
                  ].map(([weight, target]) => (
                    <tr key={weight} className="border-b border-white/5">
                      <td className="py-2 pr-4 text-white font-semibold">{weight}</td>
                      <td className="py-2">{target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-gray-400 leading-relaxed">
            Start at the lower end (0.7g/lb) and work up. Most men find 0.8g/lb is the sweet spot — enough to build muscle without feeling like you're force-feeding yourself.
          </p>
        </section>

        <section id="best-sources" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">Best Protein Sources for Men Over 60</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            Not all protein is equal. You want complete proteins — those containing all essential amino acids, especially leucine, which is the key trigger for muscle protein synthesis.
          </p>

          <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-6">
            <h3 className="text-white font-bold text-lg mb-4">Top Protein Sources (per 100g)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-[#c8a96e] font-bold py-2 pr-4">Food</th>
                    <th className="text-left text-gray-400 font-semibold py-2">Protein</th>
                    <th className="text-left text-gray-400 font-semibold py-2">Notes</th>
                  </tr>
                </thead>
                <tbody className="text-gray-400">
                  {[
                    ['Chicken breast', '31g', 'Lean, versatile, high leucine'],
                    ['Canned tuna', '30g', 'Cheap, convenient, omega-3s'],
                    ['Salmon', '25g', 'Anti-inflammatory omega-3s'],
                    ['Lean beef (90%)', '26g', 'Creatine + complete amino profile'],
                    ['Eggs (whole)', '13g', 'Most bioavailable protein source'],
                    ['Greek yogurt', '10g', 'Probiotics + casein protein'],
                    ['Cottage cheese', '11g', 'Slow-digesting casein, great before bed'],
                    ['Whey protein', '25g/scoop', 'Fast-absorbing, high leucine'],
                  ].map(([food, protein, notes]) => (
                    <tr key={food} className="border-b border-white/5">
                      <td className="py-2 pr-4 text-white font-semibold">{food}</td>
                      <td className="py-2 pr-4 text-[#c8a96e] font-bold">{protein}</td>
                      <td className="py-2 text-xs">{notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section id="timing" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">Protein Timing: Does It Matter?</h2>
          <p className="text-gray-400 leading-relaxed mb-3">
            For men over 60, protein timing matters more than it does for younger people. Research suggests that older adults need a higher per-meal protein dose to maximally stimulate muscle protein synthesis.
          </p>
          <p className="text-gray-400 leading-relaxed mb-4">
            According to <a href="https://www.health.harvard.edu/staying-healthy/how-much-protein-do-you-need-every-day" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:underline">Harvard Health</a>, spreading protein evenly across meals is more effective than eating most of it in one sitting.
          </p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 mb-4 ml-2">
            <li>Aim for 30-40g of protein per meal (not 10g at breakfast and 80g at dinner)</li>
            <li>Have a protein-rich meal or shake within 1-2 hours after training</li>
            <li>Cottage cheese or Greek yogurt before bed supports overnight muscle repair</li>
            <li>Don't skip breakfast protein — it sets the tone for the whole day</li>
          </ul>
        </section>

        <section id="sample-day" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">Sample High-Protein Day of Eating</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            Here's what hitting 160g of protein looks like for a 190-pound man. This is realistic, not extreme.
          </p>

          <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-6">
            <div className="space-y-4">
              {[
                { meal: 'Breakfast', food: '4 whole eggs + 1 cup Greek yogurt', protein: '~45g' },
                { meal: 'Lunch', food: '6oz chicken breast + 1 cup cottage cheese', protein: '~55g' },
                { meal: 'Post-Workout', food: '1 scoop whey protein shake', protein: '~25g' },
                { meal: 'Dinner', food: '6oz salmon + 1 cup edamame', protein: '~45g' },
              ].map(({ meal, food, protein }) => (
                <div key={meal} className="flex items-start justify-between border-b border-white/5 pb-3">
                  <div>
                    <p className="text-[#c8a96e] text-xs font-bold uppercase tracking-wide">{meal}</p>
                    <p className="text-gray-400 text-sm mt-1">{food}</p>
                  </div>
                  <span className="text-white font-bold text-sm whitespace-nowrap ml-4">{protein}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2">
                <span className="text-white font-bold">Total</span>
                <span className="text-[#c8a96e] font-black">~170g protein</span>
              </div>
            </div>
          </div>
        </section>

        <section id="supplements" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">When to Use Protein Supplements</h2>
          <p className="text-gray-400 leading-relaxed mb-3">
            Whole food protein is always the priority. But if you're struggling to hit your targets through food alone — which many men over 60 do, especially if appetite is lower — a quality protein supplement fills the gap.
          </p>
          <p className="text-gray-400 leading-relaxed mb-4">
            Whey protein is the gold standard: fast-absorbing, high in leucine, and well-researched. If you're lactose intolerant, whey isolate or a plant-based blend (pea + rice) works well.
          </p>
          <p className="text-gray-400 leading-relaxed">
            The <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:underline font-semibold">Over-60 Strength Blueprint</a> includes a complete nutrition guide with protein targets, meal timing, and supplement recommendations tailored specifically for men over 60.
          </p>
        </section>

        <section id="faq" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
          <div className="space-y-5">
            {[
              { q: "How much protein does a man over 60 need per day?", a: "Men over 60 who strength train should aim for 0.7-1.0 grams of protein per pound of body weight per day. A 180-pound man needs roughly 126-180 grams daily." },
              { q: "Is too much protein bad for older men?", a: "For men with healthy kidneys, higher protein intake is safe and beneficial. The concern about protein damaging kidneys applies mainly to those with pre-existing kidney disease. Always consult your doctor if you have kidney concerns." },
              { q: "What are the best protein sources for men over 60?", a: "Eggs, chicken breast, Greek yogurt, cottage cheese, salmon, lean beef, and whey protein are all excellent sources. Aim for complete proteins that contain all essential amino acids, especially leucine." },
              { q: "Should men over 60 use protein shakes?", a: "Protein shakes are a convenient tool, not a requirement. If you can hit your protein targets through whole foods, great. If not, a quality whey or plant-based protein shake can help fill the gap." },
            ].map(({ q, a }) => (
              <div key={q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
                <p className="text-white font-bold mb-2">{q}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-10 bg-[#111111] border border-[#c8a96e]/20 rounded-lg p-6">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-2">Fuel Your Results</p>
          <p className="text-white font-bold text-lg mb-2">Get The Over-60 Strength Blueprint</p>
          <p className="text-gray-400 text-sm mb-4">Includes a complete nutrition guide with protein targets and meal plans designed for men over 60 who want to build muscle and lose fat.</p>
          <a href="https://masonhalefit.com" className="inline-flex items-center gap-2 px-6 py-3 bg-[#c8a96e] text-black text-sm font-black tracking-[0.15em] uppercase rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
            Get The Program — $19.99
          </a>
        </div>

        <AuthorBio />
        <RelatedArticles articles={related} />
      </article>
    </BlogLayout>
  );
}
